import React, { useState, useEffect } from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image, pdf } from '@react-pdf/renderer';
import logoLimpia from '../assets/images/logo-limpia-xl.png';
import AforoPDFDocument, { AforoPage } from './AforoPDF';

// URL base del backend para las imágenes
const getBaseUrl = () => {
  const URL = process.env.REACT_APP_API_URL;
  const url = URL.split("/api");
  return url[0];
};

// Registrar fuentes
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 700,
    },
  ],
});

// Estilos elegantes y creativos
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    paddingTop: 15,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 5,
    backgroundColor: '#ffffff',
    lineHeight: 1.0,
  },

  // Header elegante con logo
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    backgroundColor: '#f8f9fa',
    borderRadius: 2,
    padding: 3,
    borderLeftWidth: 2,
    borderLeftColor: '#28a745',
    position: 'relative',
  },
  logoSection: {
    position: 'absolute',
    left: 10,
    alignItems: 'center',
    zIndex: 1,
  },
  logo: {
    width: 130,
    height: 20,
  },
  titleSection: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: 1500,
    color: '#28a745',
    letterSpacing: 0.5,
    fontFamily: 'Helvetica-Bold',
  },
  subtitle: {
    fontSize: 6,
    color: '#666',
    marginTop: 0.5,
  },

  // Tabla de información superior elegante
  topInfoContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 2,
    marginBottom: 3,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  topInfoTable: {
    flexDirection: 'row',
  },
  topInfoCell: {
    width: '25%',
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: '#e9ecef',
    alignItems: 'center',
  },
  topInfoCellLast: {
    width: '25%',
    padding: 4,
    alignItems: 'center',
  },
  topInfoLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#495057',
    textAlign: 'center',
    marginBottom: 3,
  },
  topInfoValue: {
    fontSize: 10,
    color: '#2c5aa0',
    textAlign: 'center',
    fontWeight: 500,
  },

  // Párrafo introductorio elegante
  introContainer: {
    backgroundColor: '#f8f9fa',
    borderRadius: 2,
    padding: 3,
    marginBottom: 3,
    borderLeftWidth: 1,
    borderLeftColor: '#28a745',
  },
  introText: {
    fontSize: 9,
    textAlign: 'justify',
    lineHeight: 1.1,
    color: '#495057',
  },

  // Sección combinada para dirección y suscriptor
  combinedInfoContainer: {
    flexDirection: 'row',
    marginBottom: 3,
    backgroundColor: '#ffffff',
    borderRadius: 2,
    borderWidth: 1,
    borderColor: '#e9ecef',
    padding: 2,
  },
  combinedInfoSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 3,
  },
  combinedInfoLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#28a745',
    marginRight: 3,
  },
  combinedInfoValue: {
    fontSize: 10,
    color: '#495057',
    flex: 1,
  },

  // Secciones principales con diseño moderno
  sectionContainer: {
    marginBottom: 1,
    backgroundColor: '#ffffff',
    borderRadius: 1,
    borderWidth: 0.5,
    borderColor: '#e9ecef',
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
  },
  sectionHeader: {
    backgroundColor: '#28a745',
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    padding: 2,
  },
  sectionTitle: {
    fontSize: 7,
    fontWeight: 700,
    textAlign: 'center',
    color: '#ffffff',
    letterSpacing: 0.2,
  },
  sectionContent: {
    padding: 2,
  },

  // Tablas modernas
  modernTable: {
    borderRadius: 3,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#dee2e6',
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
  },
  tableRowLast: {
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    padding: 3,
    borderRightWidth: 1,
    borderRightColor: '#dee2e6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableHeaderLast: {
    backgroundColor: '#f8f9fa',
    padding: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tableCell: {
    padding: 3,
    borderRightWidth: 1,
    borderRightColor: '#dee2e6',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 12,
  },
  tableCellLast: {
    padding: 3,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 12,
  },
  tableHeaderText: {
    fontSize: 7,
    fontWeight: 700,
    textAlign: 'center',
    color: '#495057',
  },
  tableCellText: {
    fontSize: 7,
    textAlign: 'center',
    color: '#6c757d',
  },

  // Layout de dos columnas
  twoColumnSection: {
    flexDirection: 'row',
    marginBottom: 2,
    gap: 4,
    width: '100%',
    marginLeft: 0,
    marginRight: 0,
  },
  leftColumn: {
    width: '40%',
  },
  rightColumn: {
    width: '60%',
  },

  // Actualización de datos (sección independiente)
  updateSection: {
    backgroundColor: '#ffffff',
    borderRadius: 2,
    padding: 2,
    marginBottom: 1,
    borderWidth: 0.5,
    borderColor: '#e9ecef',
  },
  updateTitle: {
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'center',
    color: '#28a745',
    marginBottom: 3,
    paddingBottom: 2,
    borderBottomWidth: 1,
    borderBottomColor: '#28a745',
  },
  fieldGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 3,
  },
  fieldItem: {
    width: '48%',
    marginBottom: 2,
  },
  fieldItemFifth: {
    width: '19%',
    marginBottom: 1,
  },
  fieldItemFifthSmall: {
    width: '8%',
    marginBottom: 1,
  },
  fieldItemFifthLarge: {
    width: '30%',
    marginBottom: 1,
  },
  fieldItemFull: {
    width: '100%',
    marginBottom: 2,
  },
  fieldLabel: {
    fontSize: 7,
    fontWeight: 700,
    color: '#495057',
    marginBottom: 1,
  },
  fieldValue: {
    fontSize: 9,
    color: '#6c757d',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
    paddingBottom: 2,
    minHeight: 12,
  },

  // Características del predio elegantes
  characteristicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 2,
  },
  checkboxRow: {
    flexDirection: 'row',
    width: '48%',
    marginBottom: 2,
    alignItems: 'center',
  },
  checkbox: {
    width: 10,
    height: 10,
    borderWidth: 1.5,
    borderColor: '#28a745',
    borderRadius: 2,
    marginRight: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#28a745',
    borderColor: '#28a745',
  },
  checkboxMark: {
    fontSize: 7,
    color: '#ffffff',
    fontWeight: 700,
  },
  checkboxLabel: {
    fontSize: 7,
    color: '#495057',
    flex: 1,
  },

  // Campos de texto adicionales
  textFieldContainer: {
    marginTop: 4,
    marginBottom: 4,
    paddingTop: 3,
    paddingBottom: 3,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    minHeight: 30,
  },

  // Fila de dos columnas para campos
  twoColumnRow: {
    flexDirection: 'row',
    gap: 4,
    marginTop: 4,
    marginBottom: 4,
  },
  halfWidthField: {
    width: '48%',
    paddingTop: 3,
    paddingBottom: 3,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    minHeight: 30,
  },
  textFieldLabel: {
    fontSize: 6,
    fontWeight: 700,
    color: '#28a745',
    marginBottom: 2,
  },
  textFieldValue: {
    fontSize: 9,
    color: '#495057',
    backgroundColor: '#f8f9fa',
    padding: 2,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: '#e9ecef',
    minHeight: 15,
  },

  // Sección de recipientes y sistema de recolección combinada
  recipientSystemSection: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 6,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: '#e9ecef',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
  },
  recipientSystemTitle: {
    fontSize: 6,
    fontWeight: 700,
    textAlign: 'center',
    color: '#28a745',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 2,
    borderBottomColor: '#28a745',
  },
  recipientLayout: {
    flexDirection: 'row',
    gap: 10,
  },
  recipientTableSection: {
    flex: 2,
  },
  recipientInfoSection: {
    flex: 1,
  },

  // Sistema de recolección
  recolectionSection: {
    marginTop: 8,
    padding: 8,
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
  },
  recolectionTitle: {
    fontSize: 6,
    fontWeight: 700,
    color: '#495057',
    marginBottom: 6,
    textAlign: 'center',
  },
  recolectionGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  recolectionItem: {
    width: '48%',
  },

  // Observaciones elegantes
  observationsSection: {
    backgroundColor: '#ffffff',
    borderRadius: 4,
    padding: 3,
    marginBottom: 2,
    borderWidth: 0.5,
    borderColor: '#e9ecef',
    minHeight: 15,
  },
  observationsTitle: {
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'center',
    color: '#28a745',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 2,
    borderBottomColor: '#28a745',
  },
  observationsText: {
    fontSize: 8,
    textAlign: 'justify',
    color: '#495057',
    lineHeight: 1.4,
  },

  // Firmas elegantes
  signatureSection: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    marginBottom: 0,
    borderWidth: 1,
    borderColor: '#e9ecef',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
  },
  signatureHeader: {
    backgroundColor: '#28a745',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    padding: 4,
  },
  signatureHeaderText: {
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'center',
    color: '#ffffff',
  },
  signatureBody: {
    flexDirection: 'row',
    padding: 6,
    gap: 8,
  },
  signatureColumn: {
    width: '48%',
    paddingHorizontal: 4,
  },
  signatureColumnUser: {
    width: '48%',
    paddingHorizontal: 4,
    backgroundColor: '#ecf6ec',
    borderRadius: 6,
    padding: 6,
  },
  signatureColumnUserDisabled: {
    opacity: 0.45,
  },
  signatureColumnCiudadLimpia: {
    width: '48%',
    paddingHorizontal: 4,
    backgroundColor: '#e8f5e8',
    borderRadius: 6,
    padding: 6,
  },
  watermarkContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  watermarkText: {
    fontSize: 38,
    color: '#6c757d',
    opacity: 0.10,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  signatureGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  signatureField: {
    width: '48%',
    marginBottom: 6,
  },
  signatureThreeColumnRow: {
    flexDirection: 'row',
    gap: 4,
    marginBottom: 6,
  },
  signatureThreeColumnField: {
    width: '32%',
  },
  signatureFieldFull: {
    width: '100%',
    marginBottom: 6,
  },
  signatureFieldLabel: {
    fontSize: 7,
    fontWeight: 700,
    color: '#495057',
    marginBottom: 2,
  },
  signatureFieldValue: {
    fontSize: 7,
    color: '#6c757d',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
    paddingBottom: 2,
    minHeight: 12,
  },
  signatureFieldValueStriked: {
    fontSize: 7,
    color: '#6c757d',
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6',
    paddingBottom: 2,
    minHeight: 12,
    textDecoration: 'line-through',
  },
  signatureImageContainer: {
    height: 40,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  signatureImage: {
    height: 35,
    width: 35,
    objectFit: 'contain',
  },

  // Anotaciones
  annotationsSection: {
    backgroundColor: '#ffffff',
    borderRadius: 4,
    padding: 3,
    marginBottom: 2,
    borderWidth: 0.5,
    borderColor: '#e9ecef',
    minHeight: 10,
  },
  annotationsTitle: {
    fontSize: 9,
    fontWeight: 700,
    textAlign: 'center',
    color: '#28a745',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 2,
    borderBottomColor: '#28a745',
  },

  // Estilos para servicios de recolección
  wasteTypesSection: {
    marginBottom: 4,
  },
  wasteTypeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  wasteTypeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '33%',
  },
  wasteTypeLabel: {
    fontSize: 7,
    marginLeft: 2,
    color: '#495057',
  },
  tipoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  tipoLabel: {
    fontSize: 7,
    fontWeight: 'bold',
    marginRight: 4,
    color: '#495057',
  },
  tipoLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingLeft: 4,
  },
  tipoText: {
    fontSize: 7,
    color: '#495057',
    backgroundColor: '#fff',
    paddingHorizontal: 2,
  },
  gratisValorSection: {
    marginTop: 4,
  },
  gratisValorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  gratisValorRowSeparated: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  gratisLeftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  valorRightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    maxWidth: '40%',
  },
  gratisValorLabel: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#495057',
  },
  gratisValorCheckbox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  gratisValorText: {
    fontSize: 7,
    color: '#495057',
  },
  gratisOptionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  gratisOptionLabel: {
    fontSize: 7,
    color: '#495057',
    width: 16,
    textAlign: 'left',
  },
  gratisFieldLabelFixed: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#495057',
    width: 65,
  },
  valorLine: {
    width: 80,
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingBottom: 1,
    textAlign: 'right',
  },
  valorText: {
    fontSize: 9,
    color: '#495057',
  },

  // Footer legal elegante
  footer: {
    position: 'absolute',
    bottom: 5,
    left: 20,
    right: 20,
    backgroundColor: '#f8f9fa',
    borderRadius: 6,
    padding: 5,
    borderTopWidth: 1,
    borderTopColor: '#28a745',
  },
  footerText: {
    fontSize: 6,
    textAlign: 'justify',
    lineHeight: 1.1,
    color: '#6c757d',
  },
  footerCode: {
    fontSize: 5,
    textAlign: 'right',
    marginTop: 2,
    color: '#495057',
    fontWeight: 500,
  },
});

// Componente para checkbox elegante
const CheckboxItem = ({ label, checked }) => (
  <View style={styles.checkboxRow}>
    <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
      {checked && <Text style={styles.checkboxMark}>✓</Text>}
    </View>
    <Text style={styles.checkboxLabel}>{label}</Text>
  </View>
);

// Componente para mostrar firma como imagen
const SignatureImage = ({ signaturePath, striked = false }) => {

  // Verificar diferentes tipos de datos que pueden llegar
  let finalPath = null;

  if (typeof signaturePath === 'string' && signaturePath.trim() !== '') {

    // Asegurarse de que la ruta comience con / si no es una URL completa
    if (!signaturePath.startsWith('http')) {
      const cleanPath = signaturePath.startsWith('/') ? signaturePath : `/${signaturePath}`;
      finalPath = `${getBaseUrl()}${cleanPath}`;
    } else {
      finalPath = signaturePath;
    }
  }

  // Si no hay una ruta válida, mostrar un contenedor vacío
  if (striked) {
    return (
      <View style={styles.signatureImageContainer}>
        <Text style={[styles.tableCellText, { fontSize: 6, color: '#dc3545', textDecoration: 'line-through' }]}>SIN FIRMA</Text>
      </View>
    );
  }

  if (!finalPath) {
    return (
      <View style={styles.signatureImageContainer}>
        <Text style={[styles.tableCellText, { fontSize: 6, color: '#999' }]}>Sin firma</Text>
      </View>
    );
  }

  return (
    <View style={styles.signatureImageContainer}>
      <Image
        src={finalPath}
        style={styles.signatureImage}
        onError={(error) => {
          console.error('SignatureImage - Error cargando imagen:', error);
          console.error('SignatureImage - URL que falló:', finalPath);
        }}
        onLoad={() => {
          console.log('SignatureImage - Imagen cargada exitosamente:', finalPath);
        }}
      />
    </View>
  );
};

// Función para parsear datos JSON que vienen como strings
const parseJSONData = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error('Error parsing JSON data:', error);
      return [];
    }
  }
  return [];
};

// Formatear teléfono quitando prefijo de país '57' (Colombia)
const formatPhoneWithout57 = (phone) => {
  if (phone === null || phone === undefined) return '';
  const str = String(phone).trim();
  if (str.startsWith('+57')) return str.slice(3);
  if (str.startsWith('57')) return str.slice(2);
  return str;
};

// Componente para tabla dinámica moderna
const ModernTable = ({ title, data, headers, widths, showTitle = true }) => {
  const parsedData = parseJSONData(data);

  return (
    <View style={styles.sectionContainer}>
      {showTitle && (
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>{title}</Text>
        </View>
      )}
      <View style={styles.sectionContent}>
        <View style={styles.modernTable}>
          {/* Headers */}
          <View style={styles.tableRow}>
            {headers.map((header, index) => (
              <View
                key={index}
                style={[
                  index === headers.length - 1 ? styles.tableHeaderLast : styles.tableHeader,
                  { width: widths ? widths[index] : `${100 / headers.length}%` }
                ]}
              >
                <Text style={styles.tableHeaderText}>{header}</Text>
              </View>
            ))}
          </View>

          {/* Data rows - Mostrar todas las filas */}
          {parsedData && parsedData.length > 0 ? parsedData.map((row, rowIndex) => (
            <View key={rowIndex} style={rowIndex === parsedData.length - 1 ? styles.tableRowLast : styles.tableRow}>
              {headers.map((header, colIndex) => {
                // Mapear headers a keys de datos
                let key;
                switch (header.toLowerCase()) {
                  case 'piso': key = 'piso'; break;
                  case 'ur': key = 'ur'; break;
                  case 'unr': key = 'unr'; break;
                  case 'ocupadas': key = 'ocupadas'; break;
                  case 'ocup.': key = 'ocupadas'; break;
                  case 'vacías': key = 'vacias'; break;
                  case 'actividad / nombre': key = 'actividad_nombre'; break;
                  case 'largo': key = 'largo'; break;
                  case 'ancho': key = 'ancho'; break;
                  case 'área': key = 'area'; break;
                  case 'alto': key = 'alto'; break;
                  case 'cantidad': key = 'cantidad'; break;
                  case 'm³': key = 'm3'; break;
                  case 'recipiente': key = 'descripcion'; break;
                  case 'producto': key = 'producto'; break;
                  default: key = header.toLowerCase().replace(/[^a-z0-9]/g, '_');
                }
                let value = row[key] !== undefined && row[key] !== null ? String(row[key]) : '';
                // Formatear valores de m³ con 2 decimales
                if (key === 'm3' && value && !isNaN(parseFloat(value))) {
                  value = parseFloat(value).toFixed(2);
                }
                return (
                  <View
                    key={colIndex}
                    style={[
                      colIndex === headers.length - 1 ? styles.tableCellLast : styles.tableCell,
                      { width: widths ? widths[colIndex] : `${100 / headers.length}%` }
                    ]}
                  >
                    <Text style={styles.tableCellText}>{value}</Text>
                  </View>
                );
              })}
            </View>
          )) : (
            // Fila vacía cuando no hay datos
            <View style={styles.tableRowLast}>
              {headers.map((header, colIndex) => (
                <View
                  key={colIndex}
                  style={[
                    colIndex === headers.length - 1 ? styles.tableCellLast : styles.tableCell,
                    { width: widths ? widths[colIndex] : `${100 / headers.length}%` }
                  ]}
                >
                  <Text style={styles.tableCellText}></Text>
                </View>
              ))}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

// Componente para renderizar una página de acta individual
const ActaPage = ({ actaData, casoData, isMainAct = true }) => {
  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  // Derivar partes de fecha desde fecha_visita/fecha_informe cuando falten los campos separados
  const getDerivedDateParts = () => {
    try {
      const fuente = (actaData?.fecha_visita || actaData?.fecha_informe || '').trim();
      // Caso 1: ISO yyyy-MM-dd
      if (fuente && /^\d{4}-\d{2}-\d{2}$/.test(fuente)) {
        const [yearStr, monthStr, dayStr] = fuente.split('-');
        const safeDate = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
        const dia = actaData.dia_visita || safeDate.getDate();
        const mes = actaData.mes_visita || safeDate.toLocaleDateString('es-ES', { month: 'long' });
        const año = actaData.año_visita || safeDate.getFullYear();
        return { dia, mes, año };
      }
      // Caso 2: Backend con barras (dd/MM/yyyy HH:mm o MM/dd/yyyy HH:mm)
      if (fuente && fuente.includes('/')) {
        const soloFecha = fuente.split(' ')[0];
        const [p1, p2, yy] = soloFecha.split('/');
        // Intentar dd/MM/yyyy
        const tent1 = new Date(Number(yy), Number(p2) - 1, Number(p1));
        // Intentar MM/dd/yyyy
        const tent2 = new Date(Number(yy), Number(p1) - 1, Number(p2));
        const base = isNaN(tent1.getTime()) ? tent2 : tent1;
        if (!isNaN(base.getTime())) {
          const dia = actaData.dia_visita || base.getDate();
          const mes = actaData.mes_visita || base.toLocaleDateString('es-ES', { month: 'long' });
          const año = actaData.año_visita || base.getFullYear();
          return { dia, mes, año };
        }
      }
    } catch (_) { }
    const now = new Date();
    return {
      dia: actaData.dia_visita || now.getDate(),
      mes: actaData.mes_visita || now.toLocaleDateString('es-ES', { month: 'long' }),
      año: actaData.año_visita || now.getFullYear()
    };
  };

  const { dia: diaVisitaRender, mes: mesVisitaRender, año: anioVisitaRender } = getDerivedDateParts();

  return (
    <Page size="A4" style={styles.page}>
      {/* Header elegante con logo */}
      <View style={styles.header}>
        <View style={styles.logoSection}>
          <Image
            style={styles.logo}
            src={logoLimpia}
          />
        </View>
        <View style={styles.titleSection}>
          <Text style={styles.title} >ACTA DE VISITA DIGITAL</Text>
        </View>
      </View>

      {/* Tabla de información superior */}
      <View style={styles.topInfoContainer}>
        <View style={styles.topInfoTable}>
          <View style={styles.topInfoCell}>
            <Text style={styles.topInfoLabel}>ARCHIVO No.</Text>
            <Text style={styles.topInfoValue}>{actaData.archivo_no || ''}</Text>
          </View>
          <View style={styles.topInfoCell}>
            <Text style={styles.topInfoLabel}>No. DE PQRS</Text>
            <Text style={styles.topInfoValue}>{isMainAct ? (actaData.no_pqrs || casoData.numero_reclamo || '') : ''}</Text>
          </View>
          <View style={styles.topInfoCell}>
            <Text style={styles.topInfoLabel}>CUENTA / SUSCRIPTOR</Text>
            <Text style={styles.topInfoValue}>{actaData.cuenta_suscriptor || ''}</Text>
          </View>
          <View style={styles.topInfoCellLast}>
            <Text style={styles.topInfoLabel}>CUENTA ENERGÍA</Text>
            <Text style={styles.topInfoValue}>{actaData.cuenta_energia || ''}</Text>
          </View>
        </View>
      </View>

      {/* Párrafo introductorio */}
      <View style={styles.introContainer}>
        <Text style={styles.introText}>
          En la Ciudad de <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>{actaData.ciudad_visita || 'Bogotá'}</Text>, siendo las <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>{formatTime(actaData.hora_visita) || '_____'}</Text> a los <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>{diaVisitaRender}</Text> días del mes de <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>{mesVisitaRender}</Text> del año <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>{anioVisitaRender}</Text>, se reunieron el señor <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>{actaData.sin_firma ? '________________________' : (actaData.representante_nombre || '________________________')}</Text> como representante del usuario, y el señor <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>{actaData.ciudad_limpia_nombre || '________________________'}</Text> como representante de Ciudad Limpia en su calidad de empresa prestadora del servicio de recolección, para dar constancia de los resultados obtenidos en la presente visita técnica.
        </Text>
      </View>

      {/* DIRECCIÓN Y SUSCRIPTOR EN UNA LÍNEA */}
      <View style={styles.combinedInfoContainer}>
        <View style={styles.combinedInfoSection}>
          <Text style={styles.combinedInfoLabel}>DIRECCIÓN:</Text>
          <Text style={styles.combinedInfoValue}>
            {isMainAct ?
              (actaData.direccion || casoData?.cliente?.[0]?.address?.[0]?.address || '') :
              (actaData.direccion_informe || '')
            }
          </Text>
        </View>
        <View style={styles.combinedInfoSection}>
          <Text style={styles.combinedInfoLabel}>SUSCRIPTOR:</Text>
          <Text style={styles.combinedInfoValue}>{actaData.nombre_cliente || ''}</Text>
        </View>
      </View>

      {/* Tablas de Unidades en una sola fila */}
      <View style={styles.twoColumnSection}>
        <View style={styles.leftColumn}>
          {/* Campo adicional producto_nombre */}
          <View style={styles.textFieldContainer}>
            <Text style={styles.textFieldLabel}>PRODUCTO:</Text>
            <Text style={styles.textFieldValue}>{actaData.producto_nombre || ''}</Text>
          </View>

          {/* Título con producto_nombre */}
          <View style={styles.sectionContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>UNIDADES RESIDENCIALES (UR)</Text>
            </View>
            <View style={styles.sectionContent}>
              <View style={styles.modernTable}>
                {/* Headers */}
                <View style={styles.tableRow}>
                  {['PISO', 'UR', 'OCUPADAS', 'VACÍAS'].map((header, index) => (
                    <View
                      key={index}
                      style={[
                        index === 3 ? styles.tableHeaderLast : styles.tableHeader,
                        { width: '25%' }
                      ]}
                    >
                      <Text style={styles.tableHeaderText}>{header}</Text>
                    </View>
                  ))}
                </View>

                {/* Data rows */}
                {parseJSONData(actaData.unidades_residenciales_detalle) && parseJSONData(actaData.unidades_residenciales_detalle).length > 0 ? parseJSONData(actaData.unidades_residenciales_detalle).map((row, rowIndex) => (
                  <View key={rowIndex} style={rowIndex === parseJSONData(actaData.unidades_residenciales_detalle).length - 1 ? styles.tableRowLast : styles.tableRow}>
                    {['piso', 'ur', 'ocupadas', 'vacias'].map((key, colIndex) => {
                      const value = row[key] !== undefined && row[key] !== null ? String(row[key]) : '';
                      return (
                        <View
                          key={colIndex}
                          style={[
                            colIndex === 3 ? styles.tableCellLast : styles.tableCell,
                            { width: '25%' }
                          ]}
                        >
                          <Text style={styles.tableCellText}>{value}</Text>
                        </View>
                      );
                    })}
                  </View>
                )) : (
                  <View style={styles.tableRowLast}>
                    {['piso', 'ur', 'ocupadas', 'vacias'].map((key, colIndex) => (
                      <View
                        key={colIndex}
                        style={[
                          colIndex === 3 ? styles.tableCellLast : styles.tableCell,
                          { width: '25%' }
                        ]}
                      >
                        <Text style={styles.tableCellText}></Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>

        <View style={styles.rightColumn}>
          {/* Campo adicional producto_unr */}
          <View style={styles.textFieldContainer}>
            <Text style={styles.textFieldLabel}>PRODUCTO:</Text>
            <Text style={styles.textFieldValue}>{actaData.producto_unr || ''}</Text>
          </View>


          {/* Título con producto_unr */}
          <View style={styles.sectionContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>UNIDADES NO RESIDENCIALES (UNR)</Text>
            </View>
            <View style={styles.sectionContent}>
              <View style={styles.modernTable}>
                {/* Headers */}
                <View style={styles.tableRow}>
                  {['PISO', 'UNR', 'ACTIVIDAD / NOMBRE', 'LARGO', 'ANCHO', 'ÁREA', 'OCUP.', 'VACÍAS'].map((header, index) => (
                    <View
                      key={index}
                      style={[
                        index === 7 ? styles.tableHeaderLast : styles.tableHeader,
                        { width: index === 2 ? '30%' : '10%' }
                      ]}
                    >
                      <Text style={styles.tableHeaderText}>{header}</Text>
                    </View>
                  ))}
                </View>

                {/* Data rows */}
                {parseJSONData(actaData.unidades_no_residenciales_detalle) && parseJSONData(actaData.unidades_no_residenciales_detalle).length > 0 ? parseJSONData(actaData.unidades_no_residenciales_detalle).map((row, rowIndex) => (
                  <View key={rowIndex} style={rowIndex === parseJSONData(actaData.unidades_no_residenciales_detalle).length - 1 ? styles.tableRowLast : styles.tableRow}>
                    {['piso', 'unr', 'actividad_nombre', 'largo', 'ancho', 'area', 'ocupadas', 'vacias'].map((key, colIndex) => {
                      const value = row[key] !== undefined && row[key] !== null ? String(row[key]) : '';
                      return (
                        <View
                          key={colIndex}
                          style={[
                            colIndex === 7 ? styles.tableCellLast : styles.tableCell,
                            { width: colIndex === 2 ? '30%' : '10%' }
                          ]}
                        >
                          <Text style={styles.tableCellText}>{value}</Text>
                        </View>
                      );
                    })}
                  </View>
                )) : (
                  <View style={styles.tableRowLast}>
                    {['piso', 'unr', 'actividad_nombre', 'largo', 'ancho', 'area', 'ocupadas', 'vacias'].map((key, colIndex) => (
                      <View
                        key={colIndex}
                        style={[
                          colIndex === 7 ? styles.tableCellLast : styles.tableCell,
                          { width: colIndex === 2 ? '30%' : '10%' }
                        ]}
                      >
                        <Text style={styles.tableCellText}></Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* ACTUALIZACIÓN DE DATOS (Sección independiente) */}
      <View style={styles.updateSection}>
        <Text style={styles.updateTitle}>ACTUALIZACIÓN DE DATOS</Text>

        <View style={styles.fieldGrid}>
          <View style={styles.fieldItemFifthLarge}>
            <Text style={styles.fieldLabel}>DIRECCIÓN DEL PREDIO</Text>
            <Text style={styles.fieldValue}>{actaData.direccion_predio || ''}</Text>
          </View>

          <View style={styles.fieldItemFifthSmall}>
            <Text style={styles.fieldLabel}>ESTRATO</Text>
            <Text style={styles.fieldValue}>{actaData.estrato_predio || ''}</Text>
          </View>

          <View style={styles.fieldItemFifth}>
            <Text style={styles.fieldLabel}>LOCALIDAD</Text>
            <Text style={styles.fieldValue}>{actaData.localidad_predio || ''}</Text>
          </View>

          <View style={styles.fieldItemFifth}>
            <Text style={styles.fieldLabel}>NOMBRE DEL SUSCRIPTOR</Text>
            <Text style={styles.fieldValue}>{actaData.actualizacion_nombre_suscriptor || ''}</Text>
          </View>

          <View style={styles.fieldItemFifth}>
            <Text style={styles.fieldLabel}>No. MESES A FINANCIAR</Text>
            <Text style={styles.fieldValue}>{actaData.actualizacion_numero_meses_financiar || ''}</Text>
          </View>


        </View>
      </View>

      {/* Sección de dos columnas: Características y Recipientes/Sistema */}
      <View style={styles.twoColumnSection}>
        <View style={styles.leftColumn}>
          {/* OTRAS CARACTERÍSTICAS DEL PREDIO */}
          <View style={styles.sectionContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>OTRAS CARACTERÍSTICAS DEL PREDIO</Text>
            </View>
            <View style={styles.sectionContent}>
              <View style={styles.characteristicsGrid}>
                <CheckboxItem label="ABANDONADO" checked={actaData.abandonado} />
                <CheckboxItem label="SUSCRIPTOR INACTIVO" checked={actaData.suscriptor_inactivo} />
                <CheckboxItem label="EN CONSTRUCCIÓN" checked={actaData.en_construccion} />
                <CheckboxItem label="EN REMODELACIÓN" checked={actaData.en_remodelacion} />
                <CheckboxItem label="INEXISTENTE" checked={actaData.inexistente} />
                <CheckboxItem label="INHABITABLE" checked={actaData.inhabitable_o_en_ruinas} />
                <CheckboxItem label="LOTE" checked={actaData.lote} />
                <CheckboxItem label="MÍNIMAS CONDICIONES" checked={actaData.minimas_condiciones} />
                <CheckboxItem label="PROGRAMA ICBF" checked={actaData.programa_del_icbf} />
                <CheckboxItem label="PREDIO OCUPADO" checked={actaData.predio_se_observa_ocupado} />
                <CheckboxItem label="DESCUENTO PUERTA A PUERTA." checked={actaData.descuento_puerta_a_puerta} />
                <CheckboxItem label="PQR NO ATENDIDO" checked={actaData.se_cierra_pqr_como_no_atendido} />
                <CheckboxItem label="ÁREAS COMUNES" checked={actaData.areas_comunes} />
                <CheckboxItem label="PREDIO VACÍO" checked={actaData.predio_que_se_observa_vacio} />
                <CheckboxItem label="DEMOLIDO" checked={actaData.demolido} />
                <CheckboxItem label="CUENTA ACTIVA" checked={actaData.cuenta_suscriptor_activo} />

              </View>

              {/* Campos de texto adicionales - Una fila, dos columnas */}
              <View style={styles.twoColumnRow}>

                <View style={styles.halfWidthField}>
                  <Text style={styles.textFieldLabel}>CAUSA DEMOLICIÓN:</Text>
                  <Text style={styles.textFieldValue}>{actaData.causa_demolicion || ''}</Text>
                </View>
                <View style={styles.halfWidthField}>
                  <Text style={styles.textFieldLabel}>DETALLES SUSCRIPTOR ACTIVO:</Text>
                  <Text style={styles.textFieldValue}>{actaData.suscriptor_activo_detalles || ''}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.rightColumn}>
          {/* SERVICIOS DE RECOLECCIÓN */}
          <View style={styles.sectionContainer}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>SERVICIOS DE RECOLECCIÓN</Text>
            </View>
            <View style={styles.sectionContent}>
              {/* Tipos de residuos con checkboxes */}
              <View style={styles.wasteTypesSection}>
                <View style={styles.wasteTypeRow}>
                  <View style={styles.wasteTypeItem}>
                    <View style={[styles.checkbox, actaData.tipos_residuos?.escombros && styles.checkboxChecked]}>
                      {actaData.tipos_residuos?.escombros && <Text style={styles.checkboxMark}>✓</Text>}
                    </View>
                    <Text style={styles.wasteTypeLabel}>ESCOMBROS</Text>
                  </View>
                  <View style={styles.wasteTypeItem}>
                    <View style={[styles.checkbox, actaData.tipos_residuos?.especiales && styles.checkboxChecked]}>
                      {actaData.tipos_residuos?.especiales && <Text style={styles.checkboxMark}>✓</Text>}
                    </View>
                    <Text style={styles.wasteTypeLabel}>ESPECIALES</Text>
                  </View>
                  <View style={styles.wasteTypeItem}>
                    <View style={[styles.checkbox, actaData.tipos_residuos?.para_reuso && styles.checkboxChecked]}>
                      {actaData.tipos_residuos?.para_reuso && <Text style={styles.checkboxMark}>✓</Text>}
                    </View>
                    <Text style={styles.wasteTypeLabel}>PARA RELLENO</Text>
                  </View>
                </View>
                <View style={styles.wasteTypeRow}>
                  <View style={styles.wasteTypeItem}>
                    <View style={[styles.checkbox, actaData.tipos_residuos?.para_escombrera && styles.checkboxChecked]}>
                      {actaData.tipos_residuos?.para_escombrera && <Text style={styles.checkboxMark}>✓</Text>}
                    </View>
                    <Text style={styles.wasteTypeLabel}>PARA ESCOMBRERA</Text>
                  </View>
                  <View style={styles.wasteTypeItem}>
                    <View style={[styles.checkbox, actaData.tipos_residuos?.enlonado && styles.checkboxChecked]}>
                      {actaData.tipos_residuos?.enlonado && <Text style={styles.checkboxMark}>✓</Text>}
                    </View>
                    <Text style={styles.wasteTypeLabel}>ENLONADO</Text>
                  </View>
                  <View style={styles.wasteTypeItem}>
                    <View style={[styles.checkbox, actaData.tipos_residuos?.a_granel && styles.checkboxChecked]}>
                      {actaData.tipos_residuos?.a_granel && <Text style={styles.checkboxMark}>✓</Text>}
                    </View>
                    <Text style={styles.wasteTypeLabel}>A GRANEL</Text>
                  </View>
                </View>
              </View>

              {/* Tipo */}
              <View style={styles.tipoSection}>
                <Text style={styles.tipoLabel}>TIPO </Text>
                <View >
                  <Text style={styles.tipoText}>{actaData.tipo_recoleccion || ''}</Text>
                </View>
              </View>

              {/* Tabla de recipientes */}
              <ModernTable
                title=""
                data={actaData.recipientes_detalle}
                headers={['RECIPIENTE', 'Largo', 'Ancho', 'Alto', 'CANTIDAD', 'm³']}
                widths={['25%', '15%', '15%', '15%', '15%', '15%']}
                showTitle={false}
              />

              {/* m³ GRATIS y VALOR */}
              <View style={styles.gratisValorSection}>
                <View style={styles.gratisValorRowSeparated}>
                  {/* Sección izquierda - m³ GRATIS */}
                  <View style={styles.gratisLeftSection}>
                    <Text style={styles.gratisFieldLabelFixed}>m³ GRATIS:</Text>
                    <View style={styles.gratisValorCheckbox}>
                      {/* Checkbox SI */}
                      <View style={styles.gratisOptionItem}>
                        <Text style={styles.gratisOptionLabel}>SI</Text>
                        <View style={[
                          styles.checkbox,
                          (['ESCOMBROS', 'ESPECIALES'].includes(casoData?.tipo_caso) && !!actaData?.m3_gratis) && styles.checkboxChecked
                        ]}>
                          {(['ESCOMBROS', 'ESPECIALES'].includes(casoData?.tipo_caso) && !!actaData?.m3_gratis) && <Text style={styles.checkboxMark}>✓</Text>}
                        </View>
                      </View>
                      {/* Checkbox NO */}
                      <View style={styles.gratisOptionItem}>
                        <Text style={styles.gratisOptionLabel}>NO</Text>
                        <View style={[
                          styles.checkbox,
                          (['ESCOMBROS', 'ESPECIALES'].includes(casoData?.tipo_caso) && !actaData?.m3_gratis) && styles.checkboxChecked
                        ]}>
                          {(['ESCOMBROS', 'ESPECIALES'].includes(casoData?.tipo_caso) && !actaData?.m3_gratis) && <Text style={styles.checkboxMark}>✓</Text>}
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Sección derecha - VALOR */}
                  <View style={styles.valorRightSection}>
                    <Text style={styles.gratisValorLabel}>VALOR $</Text>
                    <View style={styles.valorLine}>
                      <Text style={styles.valorText}>{actaData.cancelado ? '' : (actaData.valor_m3 ? Number(actaData.valor_m3).toLocaleString('es-CO') : '')}</Text>
                    </View>
                  </View>
                </View>

                {/* 2 m³ GRATIS debajo de m³ GRATIS */}
                <View style={[styles.gratisValorRowSeparated, { marginTop: 4 }]}>
                  {/* Sección izquierda - 2 m³ GRATIS */}
                  <View style={styles.gratisLeftSection}>
                    <Text style={styles.gratisFieldLabelFixed}>2 m³ GRATIS:</Text>
                    <View style={styles.gratisValorCheckbox}>
                      {/* Checkbox SI */}
                      <View style={styles.gratisOptionItem}>
                        <Text style={styles.gratisOptionLabel}>SI</Text>
                        <View style={[
                          styles.checkbox,
                          (['ESCOMBROS', 'ESPECIALES'].includes(casoData?.tipo_caso) && !!actaData?.dos_m3_gratis) && styles.checkboxChecked
                        ]}>
                          {(['ESCOMBROS', 'ESPECIALES'].includes(casoData?.tipo_caso) && !!actaData?.dos_m3_gratis) && <Text style={styles.checkboxMark}>✓</Text>}
                        </View>
                      </View>
                      {/* Checkbox NO */}
                      <View style={styles.gratisOptionItem}>
                        <Text style={styles.gratisOptionLabel}>NO</Text>
                        <View style={[
                          styles.checkbox,
                          (['ESCOMBROS', 'ESPECIALES'].includes(casoData?.tipo_caso) && !actaData?.dos_m3_gratis) && styles.checkboxChecked
                        ]}>
                          {(['ESCOMBROS', 'ESPECIALES'].includes(casoData?.tipo_caso) && !actaData?.dos_m3_gratis) && <Text style={styles.checkboxMark}>✓</Text>}
                        </View>
                      </View>
                    </View>
                  </View>
                </View>

                {/* Cancelado debajo de m³ GRATIS */}
                <View style={{ marginTop: 4 }}>
                  <CheckboxItem label="CANCELADO" checked={!!actaData.cancelado} />
                </View>
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* OBSERVACIONES */}
      <View style={styles.observationsSection}>
        <Text style={styles.observationsTitle}>OBSERVACIONES</Text>
        <Text style={styles.observationsText}>{actaData.observaciones || ''}</Text>
      </View>

      {/* Texto de firmas */}
      <Text style={[styles.introText, { textAlign: 'center', marginBottom: 8, fontSize: 8, fontWeight: 500 }]}>
        Firman la presente acta quienes en ella intervinieron dando constancia de lo consignado:
      </Text>

      {/* Tabla de firmas */}
      <View style={styles.signatureSection}>
        <View style={styles.signatureHeader}>
          <Text style={styles.signatureHeaderText}>FIRMAS Y DATOS DE LOS REPRESENTANTES</Text>
        </View>

        <View style={styles.signatureBody}>
          <View style={[styles.signatureColumnUser, { position: 'relative' }, actaData.sin_firma && styles.signatureColumnUserDisabled]}>
            <Text style={[styles.fieldLabel, { textAlign: 'center', marginBottom: 9, fontSize: 8, color: '#495057' }]}>REPRESENTANTE DEL USUARIO</Text>

            {actaData.sin_firma && (
              <View style={styles.watermarkContainer}>
                <Text style={styles.watermarkText}>SIN FIRMA</Text>
              </View>
            )}

            <View style={styles.signatureThreeColumnRow}>
              <View style={styles.signatureThreeColumnField}>
                <Text style={styles.signatureFieldLabel}>NOMBRE:</Text>
                <Text style={styles.signatureFieldValue}>{actaData.sin_firma ? '' : (actaData.representante_nombre || '')}</Text>
              </View>

              <View style={styles.signatureThreeColumnField}>
                <Text style={styles.signatureFieldLabel}>CÉDULA:</Text>
                <Text style={styles.signatureFieldValue}>{actaData.sin_firma ? '' : (actaData.representante_cedula || '')}</Text>
              </View>

              <View style={styles.signatureThreeColumnField}>
                <Text style={styles.signatureFieldLabel}>TELÉFONO:</Text>
                <Text style={styles.signatureFieldValue}>{actaData.sin_firma ? '' : (actaData.representante_telefono_celular || '')}</Text>
              </View>
            </View>

            <View style={styles.signatureFieldFull}>
              <Text style={styles.signatureFieldLabel}>FIRMA:</Text>
              {actaData.sin_firma ? (
                <View style={styles.signatureImageContainer} />
              ) : (
                <SignatureImage signaturePath={actaData.representante_firma} />
              )}
            </View>
          </View>

          <View style={styles.signatureColumnCiudadLimpia}>
            <Text style={[styles.fieldLabel, { textAlign: 'center', marginBottom: 9, fontSize: 8, color: '#28a745' }]}>REPRESENTANTE DE CIUDAD LIMPIA</Text>

            <View style={styles.signatureThreeColumnRow}>
              <View style={styles.signatureThreeColumnField}>
                <Text style={styles.signatureFieldLabel}>NOMBRE:</Text>
                <Text style={styles.signatureFieldValue}>{actaData.ciudad_limpia_nombre || ''}</Text>
              </View>

              <View style={styles.signatureThreeColumnField}>
                <Text style={styles.signatureFieldLabel}>CÓDIGO:</Text>
                <Text style={styles.signatureFieldValue}>{actaData.ciudad_limpia_codigo || ''}</Text>
              </View>

              <View style={styles.signatureThreeColumnField}>
                <Text style={styles.signatureFieldLabel}>TELÉFONO:</Text>
                <Text style={styles.signatureFieldValue}>{formatPhoneWithout57(actaData.ciudad_limpia_telefono || '')}</Text>
              </View>
            </View>

            <View style={styles.signatureFieldFull}>
              <Text style={styles.signatureFieldLabel}>FIRMA:</Text>
              <SignatureImage signaturePath={actaData.ciudad_limpia_firma} />
            </View>
          </View>
        </View>
      </View>

      {/* ANOTACIONES */}
      <View style={styles.annotationsSection}>
        <Text style={styles.annotationsTitle}>ANOTACIONES</Text>
        <Text style={styles.observationsText}>{actaData.anotaciones || ''}</Text>
      </View>

      {/* Footer legal elegante */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Autorizo de manera previa, expresa e informada a Ciudad Limpia para que directa o indirectamente, realice cualquier operación de tratamiento de mis Datos Personales, a nivel nacional e internacional, incluso con terceros ubicados en países que no proporcionen niveles adecuados de protección conforme a lo dispuesto en la Ley 1581 de 2012. Para conocer sobre los derechos que les asisten a los titulares de los Datos Personales, la finalidad para el tratamiento a los que los Datos Personales serán sometidos, no limitados a consultar la Política de Tratamiento de la Información de la Compañía, disponible en: Call: https://www.ciudadlimpia.com.co/uploads/documentos/politica_tratamiento_call.pdf Neiva: https://www.ciudadlimpianeiva.com.co/comunicados/PoliticasTratamientoDeLaInformacion.pdf Huila: https://www.ciudadlimpiahuila.com.co/comunicados/PoliticasTratamientoDeLaInformacion.pdf Bogotá: https://www.ciudadlimpia.com.co/PoliticasTratamientodeLainformacion.pdf
        </Text>
        <Text style={styles.footerCode}>BFCM-15</Text>
        <Text style={styles.footerCode}>09/2025 Ed.1</Text>
      </View>
    </Page>
  );
};

// Componente para la página de evidencias
const EvidenciasPage = ({ images, casoData, actaData, actaTitle = "EVIDENCIAS FOTOGRÁFICAS", actaNumber = 1, isMainAct = true }) => {
  const getBaseUrl = () => {
    const URL = process.env.REACT_APP_API_URL;
    const url = URL.split("/api");
    return url[0];
  };

  // Resolver URL de imagen robustamente (admite string absoluta/relativa u objeto con {image} o {image_url})
  const resolveImageUrl = (img) => {
    if (!img) return null;
    if (typeof img === 'object') {
      const path = img.image || img.image_url;
      return resolveImageUrl(path);
    }
    if (typeof img === 'string') {
      const trimmed = img.trim();
      if (!trimmed) return null;
      // Si ya es absoluta
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
      }
      // Asegurar prefijo '/'
      const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
      return `${getBaseUrl()}${path}`;
    }
    return null;
  };

  // Derivar objeto Date desde los datos del acta (fecha_visita/fecha_informe o partes)
  const getActaDate = () => {
    try {
      const fuente = (actaData?.fecha_visita || actaData?.fecha_informe || '').trim();
      // ISO yyyy-MM-dd
      if (fuente && /^\d{4}-\d{2}-\d{2}$/.test(fuente)) {
        const [y, m, d] = fuente.split('-');
        const safeDate = new Date(Number(y), Number(m) - 1, Number(d));
        if (!isNaN(safeDate.getTime())) return safeDate;
      }
      // Formatos con barras: dd/MM/yyyy HH:mm o MM/dd/yyyy HH:mm
      if (fuente && fuente.includes('/')) {
        const soloFecha = fuente.split(' ')[0];
        const [p1, p2, yy] = soloFecha.split('/');
        const tent1 = new Date(Number(yy), Number(p2) - 1, Number(p1));
        const tent2 = new Date(Number(yy), Number(p1) - 1, Number(p2));
        const base = isNaN(tent1.getTime()) ? tent2 : tent1;
        if (!isNaN(base.getTime())) return base;
      }
      // Partes separadas con mes en texto
      if (actaData?.dia_visita && actaData?.mes_visita && actaData?.año_visita) {
        const monthNames = {
          'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3, 'mayo': 4, 'junio': 5,
          'julio': 6, 'agosto': 7, 'septiembre': 8, 'setiembre': 8, 'octubre': 9,
          'noviembre': 10, 'diciembre': 11
        };
        const mes = String(actaData.mes_visita || '').toLowerCase();
        const mesIdx = monthNames[mes];
        const d = Number(actaData.dia_visita);
        const y = Number(actaData.año_visita);
        const composed = new Date(y, mesIdx ?? 0, d);
        if (!isNaN(composed.getTime())) return composed;
      }
    } catch (_) { }
    return new Date();
  };

  const actaDateObj = getActaDate();
  const actaDateStr = actaDateObj.toLocaleDateString('es-ES');

  return (
    <Page size="A4" style={styles.page}>
      {/* Header elegante con logo */}
      <View style={styles.header}>
        <View style={styles.logoSection}>
          <Image style={styles.logo} src={logoLimpia} />
        </View>
        <View style={styles.titleSection}>
          <Text style={styles.title}>EVIDENCIAS FOTOGRÁFICAS</Text>
          <Text style={styles.subtitle}>Registro visual de la visita técnica - {actaTitle}</Text>
        </View>
      </View>

      {/* Información del caso */}
      <View style={styles.topInfoContainer}>
        <View style={styles.topInfoTable}>
          <View style={styles.topInfoCell}>
            <Text style={styles.topInfoLabel}>OT</Text>
            <Text style={styles.topInfoValue}>{casoData?.name?.replace(/^Caso\s/, '') || ''}</Text>
          </View>
          <View style={styles.topInfoCell}>
            <Text style={styles.topInfoLabel}>RECLAMO</Text>
            <Text style={styles.topInfoValue}>{isMainAct ? (casoData?.numero_reclamo || '') : ''}</Text>
          </View>
          <View style={styles.topInfoCell}>
            <Text style={styles.topInfoLabel}>FECHA</Text>
            <Text style={styles.topInfoValue}>{actaDateStr}</Text>
          </View>
          <View style={styles.topInfoCellLast}>
            <Text style={styles.topInfoLabel}>ACTA #{actaNumber}</Text>
            <Text style={styles.topInfoValue}>{images?.length || 0} evidencias</Text>
          </View>
        </View>
      </View>

      {/* Sección de evidencias fotográficas */}
      <View style={styles.sectionContainer}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>EVIDENCIAS FOTOGRÁFICAS - {actaTitle}</Text>
        </View>
        <View style={styles.sectionContent}>
          {images && images.length > 0 ? (
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 8,
              justifyContent: 'flex-start',
              alignItems: 'flex-start'
            }}>
              {images.map((imageObj, index) => (
                <View key={imageObj.id || index} style={{
                  width: '48%',
                  marginBottom: 12,
                  backgroundColor: '#ffffff',
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: '#dee2e6',
                  overflow: 'hidden',
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.08,
                  shadowRadius: 4
                }}>
                  {/* Header de la evidencia */}
                  <View style={{
                    backgroundColor: '#28a745',
                    padding: 4,
                    alignItems: 'center'
                  }}>
                    <Text style={{
                      fontSize: 8,
                      fontWeight: 700,
                      textAlign: 'center',
                      color: '#ffffff',
                      letterSpacing: 0.3
                    }}>
                      EVIDENCIA {index + 1} - ACTA #{actaNumber}
                    </Text>
                  </View>

                  {/* Contenedor de la imagen */}
                  <View style={{
                    height: 180,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa',
                    padding: 6
                  }}>
                    <Image
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: 3,
                        borderWidth: 1,
                        borderColor: '#e9ecef'
                      }}
                      src={resolveImageUrl(imageObj.image || imageObj.image_url || imageObj)}
                      onError={(error) => {
                        console.error('Error cargando imagen de evidencia:', error);
                        console.error('URL que falló:', resolveImageUrl(imageObj.image || imageObj.image_url || imageObj));
                      }}
                      onLoad={() => {
                        console.log('Imagen de evidencia cargada exitosamente:', resolveImageUrl(imageObj.image || imageObj.image_url || imageObj));
                      }}
                    />
                  </View>

                  {/* Footer con información */}
                  <View style={{
                    padding: 4,
                    backgroundColor: '#f8f9fa',
                    borderTopWidth: 1,
                    borderTopColor: '#dee2e6',
                    alignItems: 'center'
                  }}>
                    <Text style={{
                      fontSize: 7,
                      textAlign: 'center',
                      color: '#6c757d',
                      fontWeight: 500
                    }}>
                      Capturada: {actaDateStr}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <View style={{
              width: '100%',
              padding: 24,
              backgroundColor: '#f8f9fa',
              borderRadius: 6,
              borderWidth: 1,
              borderColor: '#dee2e6',
              alignItems: 'center',
              borderLeftWidth: 4,
              borderLeftColor: '#28a745'
            }}>
              <Text style={{
                fontSize: 10,
                color: '#6c757d',
                textAlign: 'center',
                fontWeight: 500,
                marginBottom: 4
              }}>
                Sin evidencias fotográficas
              </Text>
              <Text style={{
                fontSize: 8,
                color: '#adb5bd',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                No se registraron imágenes para {actaTitle.toLowerCase()}
              </Text>
            </View>
          )}
        </View>
      </View>

      {/* Footer con información adicional */}
      <View style={{
        position: 'absolute',
        bottom: 15,
        left: 20,
        right: 20,
        backgroundColor: '#f8f9fa',
        borderRadius: 4,
        padding: 6,
        borderTopWidth: 2,
        borderTopColor: '#28a745'
      }}>
        <Text style={{
          fontSize: 7,
          textAlign: 'center',
          color: '#6c757d',
          lineHeight: 1.2
        }}>
          Las evidencias fotográficas de {actaTitle.toLowerCase()} forman parte integral del acta de visita y constituyen
          registro documental de las condiciones encontradas durante la inspección técnica.
        </Text>
      </View>
    </Page>
  );
};

// Componente principal que maneja múltiples actas
const ActaVisitaPDF = ({ actaData, casoData, actasAdicionales = [] }) => {
  // Log para debugging

  // Log detallado de imágenes por acta adicional
  if (actasAdicionales && actasAdicionales.length > 0) {
    actasAdicionales.forEach((acta, index) => {
      console.log(`ActaVisitaPDF - Acta adicional ${index + 1}:`, {
        id: acta.id,
        titulo: acta.titulo,
        images: acta.images,
        cantidadImagenes: acta.images?.length || 0
      });
    });
  }

  // Validar si existe un aforo
  const tieneAforo = casoData?.aforos && casoData.aforos.length > 0 &&
    casoData.aforos[0]?.visitas && casoData.aforos[0].visitas.length > 0;

  // Obtener las imágenes del informe principal
  const imagenesInformePrincipal = casoData?.informe?.images || [];
  // Obtener las imágenes a nivel de OT (caso)
  const imagenesOT = casoData?.images || [];

  return (
    <Document>
      {/* Página principal - Acta principal */}
      <ActaPage actaData={actaData} casoData={casoData} isMainAct={true} />

      {/* Evidencias del acta principal - Solo si hay imágenes del informe principal */}
      {imagenesInformePrincipal && imagenesInformePrincipal.length > 0 && (
        <EvidenciasPage
          images={imagenesInformePrincipal}
          casoData={casoData}
          actaData={actaData}
          actaTitle="ACTA PRINCIPAL"
          actaNumber={1}
          isMainAct={true}
        />
      )}

      {/* Páginas adicionales - Una por cada acta adicional con sus evidencias */}
      {actasAdicionales && actasAdicionales.length > 0 && actasAdicionales.map((actaAdicional, index) => {
        // Obtener las imágenes específicas de esta acta adicional
        const imagenesActaAdicional = actaAdicional?.images || [];

        console.log(`Renderizando acta adicional ${index + 1}:`, {
          id: actaAdicional.id,
          titulo: actaAdicional.titulo,
          cantidadImagenes: imagenesActaAdicional.length,
          images: imagenesActaAdicional
        });

        return (
          <React.Fragment key={`acta-adicional-${index}`}>
            {/* Página del acta adicional */}
            <ActaPage
              actaData={actaAdicional}
              casoData={casoData}
              isMainAct={false}
            />

            {/* Evidencias de esta acta adicional - Solo si tiene imágenes */}
            {imagenesActaAdicional && imagenesActaAdicional.length > 0 && (
              <EvidenciasPage
                images={imagenesActaAdicional}
                casoData={casoData}
                actaData={actaAdicional}
                actaTitle={`ACTA ADICIONAL ${index + 1}`}
                actaNumber={index + 2}
                isMainAct={false}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Página de aforo - Solo si existe un aforo */}
      {tieneAforo && (
        <AforoPage
          visitas={casoData.aforos[0].visitas}
          casoData={casoData}
          recipientesData={[]}
        />
      )}

      {/* Página final con evidencias de la OT */}
      {imagenesOT && imagenesOT.length > 0 && (
        <EvidenciasPage
          images={imagenesOT}
          casoData={casoData}
          actaData={actaData}
          actaTitle="OT"
          actaNumber={(actasAdicionales?.length || 0) + 2}
          isMainAct={true}
        />
      )}
    </Document>
  );
};

// Función para generar y descargar el PDF
export const generateActaVisitaPDF = async (actaData, casoData, actasAdicionales = []) => {
  try {
    const doc = <ActaVisitaPDF actaData={actaData} casoData={casoData} actasAdicionales={actasAdicionales} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();

    // Crear URL para descarga
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Acta_Visita_${casoData?.numero_reclamo || 'documento'}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error generando PDF:', error);
    return { success: false, error };
  }
};

// Función para generar PDF como blob para envío al backend
export const generateActaVisitaPDFBlob = async (actaData, casoData, actasAdicionales = []) => {
  try {
    const doc = <ActaVisitaPDF actaData={actaData} casoData={casoData} actasAdicionales={actasAdicionales} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();

    return { success: true, blob };
  } catch (error) {
    console.error('Error generando PDF blob:', error);
    return { success: false, error };
  }
};

// Componente PDF sin página de evidencias para envío por WhatsApp
const ActaVisitaPDFWithoutImages = ({ actaData, casoData, actasAdicionales = [] }) => {
  // Validar si existe un aforo
  const tieneAforo = casoData?.aforos && casoData.aforos.length > 0 &&
    casoData.aforos[0]?.visitas && casoData.aforos[0].visitas.length > 0;

  return (
    <Document>
      {/* Página principal - Acta principal */}
      <ActaPage actaData={actaData} casoData={casoData} isMainAct={true} />

      {/* Páginas adicionales - Una por cada acta adicional */}
      {actasAdicionales && actasAdicionales.length > 0 && actasAdicionales.map((actaAdicional, index) => (
        <ActaPage
          key={`acta-adicional-${index}`}
          actaData={actaAdicional}
          casoData={casoData}
          isMainAct={false}
        />
      ))}

      {/* NO incluir página de evidencias para envío por WhatsApp */}

      {/* Página de aforo - Solo si existe un aforo */}
      {tieneAforo && (
        <AforoPage
          visitas={casoData.aforos[0].visitas}
          casoData={casoData}
          recipientesData={[]}
        />
      )}
    </Document>
  );
};

// Función para generar PDF como blob SIN imágenes para envío por WhatsApp
export const generateActaVisitaPDFBlobWithoutImages = async (actaData, casoData, actasAdicionales = []) => {
  try {
    const doc = <ActaVisitaPDFWithoutImages actaData={actaData} casoData={casoData} actasAdicionales={actasAdicionales} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();

    return { success: true, blob };
  } catch (error) {
    console.error('Error generando PDF blob sin imágenes:', error);
    return { success: false, error };
  }
};


// Función para abrir el PDF en nueva ventana
export const openActaVisitaPDF = async (actaData, casoData, actasAdicionales = []) => {
  try {
    console.log('openActaVisitaPDF - Parámetros recibidos:');
    console.log('- actaData:', actaData);
    console.log('- casoData:', casoData);
    console.log('- actasAdicionales:', actasAdicionales);
    console.log('- actasAdicionales length:', actasAdicionales?.length || 0);
    console.log('- actasAdicionales es array:', Array.isArray(actasAdicionales));

    const doc = <ActaVisitaPDF actaData={actaData} casoData={casoData} actasAdicionales={actasAdicionales} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();

    // Crear URL para abrir en nueva ventana
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');

    if (!newWindow) {
      alert('Por favor, permite las ventanas emergentes para abrir el PDF');
      return { success: false, error: 'Popup blocked' };
    }

    // Limpiar URL después de un tiempo
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 10000);

    return { success: true };
  } catch (error) {
    console.error('Error abriendo PDF:', error);
    return { success: false, error };
  }
};

// Componente PDF para acta adicional individual (sin acta principal)
const ActaAdicionalIndividualPDF = ({ actaData, casoData }) => {
  return (
    <Document>
      <ActaPage actaData={actaData} casoData={casoData} isMainAct={false} />
      {actaData?.images && actaData.images.length > 0 && (
        <EvidenciasPage
          images={actaData.images}
          casoData={casoData}
          actaData={actaData}
          actaTitle="ACTA ADICIONAL"
          actaNumber={1}
          isMainAct={false}
        />
      )}
    </Document>
  );
};

// Función para generar PDF de acta adicional individual
export const generateActaAdicionalIndividualPDFBlob = async (actaData, casoData) => {
  try {
    const doc = <ActaAdicionalIndividualPDF actaData={actaData} casoData={casoData} />;
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();
    return { success: true, blob };
  } catch (error) {
    console.error('Error generando PDF de acta adicional individual:', error);
    return { success: false, error };
  }
};

export default ActaVisitaPDF;
